import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, inr, customerSession, restaurantSession } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Printer, Download, ArrowLeft } from "lucide-react";

export default function InvoicePage() {
  const [params] = useSearchParams();
  const orderId = params.get("order");
  const audience = params.get("as") || "customer"; // customer | restaurant
  const [inv, setInv] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!orderId) return;
    const url = audience === "restaurant"
      ? `/restaurant/orders/${orderId}/invoice`
      : `/orders/${orderId}/invoice`;
    api.get(url).then((r) => setInv(r.data)).catch((e) => {
      setErr(e.response?.data?.detail || "Cannot load invoice");
    });
  }, [orderId, audience]);

  const printInvoice = () => window.print();

  if (err)
    return (
      <div className="min-h-screen grid place-items-center bg-brand-light p-6">
        <div className="text-center">
          <div className="font-heading text-2xl font-bold text-brand-dark">Invoice unavailable</div>
          <div className="text-sm text-brand-dark/60 mt-2">{err}</div>
          <button onClick={() => window.history.back()} className="mt-4 text-brand-orange text-sm">← Go back</button>
        </div>
      </div>
    );
  if (!inv)
    return <div className="min-h-screen grid place-items-center">Loading…</div>;

  const paidAt = inv.paid_at ? new Date(inv.paid_at) : null;

  return (
    <div className="min-h-screen bg-brand-light">
      <style>{`@media print { .no-print { display: none !important; } body { background: white !important; } }`}</style>
      <div className="no-print bg-white border-b border-brand-dark/10 sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <button onClick={() => window.history.back()} className="text-sm text-brand-dark/70 hover:text-brand-orange flex items-center gap-1" data-testid="inv-back">
            <ArrowLeft size={16} /> Back
          </button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={printInvoice} data-testid="inv-print">
              <Printer size={14} className="mr-1" /> Print
            </Button>
            <Button onClick={printInvoice} className="bg-brand-orange text-white hover:bg-brand-orange-dark" data-testid="inv-download">
              <Download size={14} className="mr-1" /> Save as PDF
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto p-4 lg:p-8">
        <div className="bg-white rounded-2xl border border-brand-dark/10 p-6 lg:p-10 shadow-sm print:shadow-none print:border-0">
          {/* Header */}
          <div className="flex items-start justify-between pb-4 border-b border-brand-dark/10">
            <div>
              {inv.restaurant?.logo_url && (
                <img src={inv.restaurant.logo_url} alt="" className="w-14 h-14 rounded-full object-cover mb-2" />
              )}
              <div className="font-heading text-2xl font-bold text-brand-dark" data-testid="inv-restaurant-name">
                {inv.restaurant?.name}
              </div>
              <div className="text-xs text-brand-dark/60 mt-0.5">
                {inv.restaurant?.address}
                {inv.restaurant?.city ? `, ${inv.restaurant.city}` : ""}
              </div>
              {inv.restaurant?.gst_number && (
                <div className="text-xs text-brand-dark/60 mt-0.5">GSTIN: {inv.restaurant.gst_number}</div>
              )}
            </div>
            <div className="text-right">
              <div className="text-xs uppercase tracking-widest text-brand-orange">Invoice</div>
              <div className="font-heading text-xl font-bold text-brand-dark mt-1" data-testid="inv-number">
                {inv.invoice_number}
              </div>
              <div className="text-xs text-brand-dark/60 mt-0.5">Order {inv.order_no}</div>
              {inv.table_number && (
                <div className="text-xs text-brand-dark/60">Table #{inv.table_number}</div>
              )}
            </div>
          </div>

          {/* Bill To / Meta */}
          <div className="grid sm:grid-cols-2 gap-4 mt-4 text-sm">
            <div>
              <div className="text-xs uppercase tracking-widest text-brand-dark/50 mb-1">Bill to</div>
              <div className="font-medium text-brand-dark">
                {inv.customer?.name || "Guest"}
              </div>
              {inv.customer?.phone && (
                <div className="text-xs text-brand-dark/60">{inv.customer.phone}</div>
              )}
            </div>
            <div className="sm:text-right">
              <div className="text-xs uppercase tracking-widest text-brand-dark/50 mb-1">Details</div>
              <div className="text-brand-dark">
                {paidAt ? paidAt.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—"}
              </div>
              <div className="text-xs text-brand-dark/60">
                Payment: {(inv.payment_method || "cash").replace(/_/g, " ")} ·{" "}
                <span className="text-green-600 font-medium uppercase">{inv.payment_status}</span>
              </div>
              <div className="text-xs mt-1">
                <span className="inline-block bg-brand-orange/10 text-brand-orange px-2 py-0.5 rounded-full font-medium" data-testid="inv-order-type">
                  {inv.order_type === "takeaway" ? "Takeaway" : "Dine-In"}
                </span>
              </div>
            </div>
          </div>

          {/* Items table */}
          <div className="mt-6">
            <div className="border-t border-brand-dark/10">
              <div className="grid grid-cols-12 gap-2 py-2 text-xs uppercase tracking-widest text-brand-dark/50 border-b border-brand-dark/10">
                <div className="col-span-6">Item</div>
                <div className="col-span-2 text-right">Qty</div>
                <div className="col-span-2 text-right">Price</div>
                <div className="col-span-2 text-right">Total</div>
              </div>
              {inv.items.map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 py-2 text-sm border-b border-brand-dark/5" data-testid={`inv-line-${i}`}>
                  <div className="col-span-6 text-brand-dark">
                    {it.item_name}
                    {it.special_note && <div className="text-xs text-brand-dark/50">Note: {it.special_note}</div>}
                  </div>
                  <div className="col-span-2 text-right">{it.quantity}</div>
                  <div className="col-span-2 text-right">{inr(it.item_price)}</div>
                  <div className="col-span-2 text-right font-medium">{inr(it.line_total)}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Totals */}
          <div className="mt-4 flex justify-end">
            <div className="w-full sm:w-64 text-sm">
              <div className="flex justify-between py-1">
                <span className="text-brand-dark/70">Subtotal</span>
                <span>{inr(inv.subtotal)}</span>
              </div>
              <div className="flex justify-between py-1">
                <span className="text-brand-dark/70">GST</span>
                <span>{inr(inv.tax_amount)}</span>
              </div>
              {(inv.custom_charges || []).map((c, i) => (
                <div key={i} className="flex justify-between py-1 text-brand-dark/70" data-testid={`inv-charge-${i}`}>
                  <span>{c.name}{c.type === "percentage" && <span className="text-xs"> ({c.value}%)</span>}</span>
                  <span>{inr(c.amount)}</span>
                </div>
              ))}
              {inv.discount_amount > 0 && (
                <div className="flex justify-between py-1 text-brand-orange">
                  <span>Discount</span>
                  <span>−{inr(inv.discount_amount)}</span>
                </div>
              )}
              <div className="flex justify-between py-2 border-t border-brand-dark/10 mt-1 font-heading font-bold text-lg" data-testid="inv-total">
                <span>Total paid</span>
                <span>{inr(inv.total_amount)}</span>
              </div>
            </div>
          </div>

          <div className="mt-6 text-center text-xs text-brand-dark/50">
            Thank you for dining with us. ✦ Powered by tabdine
          </div>
        </div>
      </div>
    </div>
  );
}
